// Original product-mechanics lessons. All worked figures are hypothetical.
export const productLessons = [
  {
    id: 'tracking-difference', moduleId: 'products',
    title: 'ETF 跟指數差多少，和差得穩不穩', subtitle: '追蹤差距與追蹤誤差', minutes: 7, tag: '比較績效',
    objectives: ['分開解讀一段期間的報酬差距與差距的波動', '先對齊幣別、期間與含息口徑再比較'],
    paragraphs: [
      '本課把 Tracking difference（TD）稱為追蹤差距，定義為同期間基金報酬減去指數報酬。負值代表落後，正值代表領先。不同資料商可能採相反減法，閱讀數字前先查定義；基金淨值報酬也不等於你在市場實際買賣的報酬。',
      'Tracking error（TE）在本課採統計定義：一連串各期「基金報酬減指數報酬」的標準差，描述差距的變動程度。它不是兩個累積報酬相減。若每期總是落後同樣幅度，差距很穩，TE 可以很低，但仍持續落後；比較 TE 還要核對日或月資料、樣本期間及是否年化。',
      '費用、現金部位、複製方式、調整持股的時點，都可能影響追蹤。先確認兩邊都是同幣別、同期間，並對齊股息再投資及稅前稅後口徑。拿不含息的價格指數，和已含配息再投資的 ETF 報酬相比，容易誤認基金管理創造了額外成果。',
    ],
    example: {
      title: '假設：落後幅度與穩定程度要分開看',
      steps: ['同一年、同幣別與含息口徑下，ETF 淨值總報酬 7.6%，指數 8%，TD 為 −0.4 個百分點。', '另一組獨立的三個月資料中，甲的每月主動報酬為 −0.12、−0.12、−0.12 個百分點，三期差距完全相同，TE 為 0。', '乙的每月主動報酬為 −0.36、0.12、−0.12 個百分點，平均也是 −0.12 個百分點，但差距有起伏，TE 大於 0。'],
      conclusion: '每月差距的平均不是全年複利追蹤差距；TE 為零也不表示報酬沒有落後。',
    },
    pitfalls: ['把 TE 當成基金會虧多少，忽略它衡量的是相對指數的差距波動。', '比較不同幣別或不同含息口徑，卻把落差全部歸因於基金費用。'],
    glossary: [{ term: '主動報酬', definition: '某一期投資組合報酬減去比較基準報酬，本課用百分點表示差值。' }, { term: '標準差', definition: '衡量一組數值相對其平均值分散程度的統計量。' }],
    takeaway: 'TD 問「這段時間差多少」，TE 問「各期差距變動多大」。',
    exercise: '找一份被動 ETF 月報，記下基準名稱、幣別、含息方式與績效期間；若有 TE，另外抄下計算頻率和年化說明。',
    quiz: { question: '各月基金都比指數少 0.2 個百分點，這組每月主動報酬的標準差是多少？', options: ['每月 0.2%', '0', '無法區分追蹤差距與波動'], answer: 1, explanation: '所有觀察值都是 −0.2 個百分點，沒有相對平均值的偏離，所以標準差為 0；基金仍每月落後，不能因此說 TD 也為 0。' },
    sources: [
      { label: '市場先生｜ETF 追蹤差異的主題閱讀', url: 'https://rich01.com/what-etf-tracking-error/', kind: 'reading' },
      { label: 'DWS｜公開說明書中的 TD 與 TE 定義', url: 'https://etf.dws.com/download/asset/97f28cea-2f24-445d-b135-f9c31a5f884d', kind: 'official' },
      { label: 'iShares｜比較 ETF 的成本與追蹤指標', url: 'https://www.ishares.com/uk/individual/en/education/getting-started-with-etfs/investor-education/compare-global-investment-options', kind: 'official' },
    ],
  },
  {
    id: 'etf-distributions', moduleId: 'products',
    title: '領到配息，資產就一定變多嗎？', subtitle: '配息來源、除息與總報酬', minutes: 7, tag: '拆解配息',
    objectives: ['把配息現金與剩餘基金價值一起計算', '辨認年化配息率的假設與配息組成'],
    paragraphs: [
      '基金配息是把一部分資產分配給受益人，不是憑空增加價值。其他條件不變時，每單位淨值會反映這筆分配而減少。除息的理論調整和市場成交價是不同事情：實際買賣價格還受資產行情、供需與折溢價影響，不能保證剛好減去配息金額。',
      '配息率描述分配金額相對某個價格或淨值的比例，分母與採用期間要看資料定義。若把一次月配息乘以 12，只是在假設之後每次都相同，既不是未來配息承諾，也不是年化總報酬。檢查成果仍需把期末價值、期間分配和原投入一起看，並納入實際費用。',
      '配息來源可能包括股利、利息、已實現資本利得或其他依基金規則可分配的項目；看發行商公告的組成，不能只憑淨值下降推斷來源。除息日、收益分配基準日與發放日各有用途，取得分配權不表示當天現金已進銀行帳戶。',
    ],
    example: {
      title: '假設：把基金與待領配息放在同一本帳',
      steps: ['持有 100 單位，除息前每單位淨值 25 元，價值為 2,500 元；假設每單位配息 1 元。', '只考慮分配、沒有行情與費用變化時，除息後理論淨值為 24 元，基金價值 2,400 元，另有待領分配 100 元，合計仍為 2,500 元。', '若配息發放後，期末每單位淨值降為 23.5 元，且期間無其他現金流，總價值為 2,350 + 100 = 2,450 元，這段期間的淨值總報酬為 −2%。'],
      conclusion: '收到配息 100 元和整體虧損 50 元可以同時發生；這裡以淨值計算，實際賣出結果還要看成交價及費用。',
    },
    pitfalls: ['看到月配就把每月金額當成固定薪水，沒有查看分配條件。', '把配息率、債券 YTM 與含息總報酬當成可直接互換的數字。'],
    glossary: [{ term: '收益分配組成', definition: '公告這次分配由哪些收益或其他允許項目構成；預估組成可能與最終資料不同。' }, { term: '除息日', definition: '當次收益分配權從基金單位分離的日期，和現金實際發放日不必相同。' }],
    takeaway: '配息要和剩下的資產一起算，不能只看入帳通知。',
    exercise: '找一份 ETF 配息公告，圈出每單位金額、除息日、發放日與組成；再確認網站顯示的配息率分母是價格還是淨值。',
    quiz: { question: '忽略費用，投入 2,000 元，期末持有價值 1,850 元，期間領到配息 100 元，沒有其他現金流，總報酬為何？', options: ['5%', '−7.5%', '−2.5%'], answer: 2, explanation: '（1,850 + 100 − 2,000）÷ 2,000 = −2.5%。只拿 100 ÷ 2,000 = 5%，會漏掉資產價值的變化。' },
    sources: [
      { label: '市場先生｜配息型基金的主題閱讀', url: 'https://rich01.com/fund-with-dividend-vs/', kind: 'reading' },
      { label: '證交所｜收益分配組成與配息觀念', url: 'https://www.twse.com.tw/downloads/zh/ETF/concept06.pdf', kind: 'official' },
      { label: '證交所 e 添富｜配息日期與組成查詢', url: 'https://www.twse.com.tw/zh/ETFortune/dividendList', kind: 'official' },
    ],
  },
  {
    id: 'bond-yields', moduleId: 'products',
    title: '同一張債券，為什麼有三種利率？', subtitle: '票面利率、當期殖利率與 YTM', minutes: 8, tag: '讀懂殖利率',
    objectives: ['分辨三個比率各自使用的分母與現金流', '理解到期殖利率不是無條件報酬承諾'],
    paragraphs: [
      '固定利率債券的票面利率，決定每年利息相對面額的比例。你可能以高於或低於面額的市價買進，因此「每年利息 ÷ 目前價格」形成另一個數字：當期殖利率。它著重利息收入，尚未計入買價與最後償還金額的差額。',
      '到期殖利率（YTM）進一步把約定利息、還本金額與時間放在一起，是讓未來約定現金流的折現價值等於買價的折現率。它以按約付款並持有至到期為情境；若中途出售、違約或被提前贖回，結果會不同，實際費用也會減少你收到的淨報酬。',
      'YTM 作為現金流的內部報酬率，不等於存款保證利率。若要讓整段投資的最終累積財富達到這個複利速度，中途收到的票息還要以相同收益率再投資；未來能否做到並不確定。以下用只剩一年、所有款項在期末支付的簡化例子，避免中途再投資的干擾。',
    ],
    example: {
      title: '假設：花 960 元買一張一年後到期的債券',
      steps: ['面額 1,000 元，固定票面利率 4%，一年後按約收到利息 40 元和本金 1,000 元；假設無提前贖回、應計利息與費用。', '當期殖利率 = 40 ÷ 960，約 4.17%，只看一年利息相對買價。', '因為全部 1,040 元在一年後收到，YTM = 1,040 ÷ 960 − 1，約 8.33%；其中包括買價低於還本金額的差額。'],
      conclusion: '4%、4.17% 與 8.33% 都可能正確，但回答不同問題；按約收回的假設不能省略。',
    },
    pitfalls: ['以為票面利率高，任何買入價格的總報酬都會比較高。', '把債券投資組合的 YTM 當成 ETF 每年會配發的固定比率。'],
    glossary: [{ term: '面額', definition: '本課簡化債券約定到期償還的本金基礎，可能與你的買入價格不同。' }, { term: '再投資風險', definition: '收到利息或提前收回本金後，未必能以原先假設的收益率繼續投資。' }],
    takeaway: '看到殖利率，先確認它算了哪些現金流、用哪個價格，以及成立條件。',
    exercise: '用面額 2,000 元、年息 60 元、市價 1,920 元，算出票面利率與當期殖利率；再列出計算 YTM 還缺哪些日期及付款條件。',
    quiz: { question: '面額 1,000 元、年利息 50 元、市價 1,250 元的固定利率債券，當期殖利率是多少？', options: ['4%', '5%', '25%'], answer: 0, explanation: '當期殖利率 = 50 ÷ 1,250 = 4%；5% 是票面利率。沒有到期時間及其他付款資訊，不能由這裡直接得到 YTM。' },
    sources: [
      { label: '市場先生｜債券利率的主題閱讀', url: 'https://rich01.com/4-types-bond-rate/', kind: 'reading' },
      { label: 'Charles Schwab｜債券殖利率的不同計算方式', url: 'https://www.schwab.com/learn/story/understanding-bond-yield-measurements', kind: 'official' },
      { label: 'Fidelity｜YTM 假設與債券風險說明', url: 'https://www.fidelity.com/fixed-income-bonds/fi-common-risk', kind: 'official' },
    ],
  },
  {
    id: 'duration-risk', moduleId: 'products',
    title: '利率動一點，債券價格為何動很多？', subtitle: '用存續期間估計價格敏感度', minutes: 7, tag: '利率風險',
    objectives: ['區分到期年限與價格敏感度', '把利率的百分點變化代入簡化估算'],
    paragraphs: [
      '到期年限是距離約定還本金日多久；存續期間（Duration）則把付款時點、票息及殖利率等因素納入衡量。兩張都十年後到期的債券，票息不同，對利率變化的反應就可能不同，不能直接把「十年期」當成存續期間十年。',
      '本課使用修正存續期間 D，對小幅殖利率變動估計：價格變動率約等於 −D × 殖利率變化。把 0.5 個百分點寫成小數 0.005 後再代入。這是其他條件不變下的局部估算，描述價格的即時敏感度，沒有加上往後領取的利息。',
      '大幅變動時，價格與殖利率關係的彎曲程度（凸性）會讓估算偏離；基金的不同到期段利率也未必一起變動。有提前贖回等特性的商品常需看有效存續期間，閱讀月報時應核對指標名稱。存續期間低仍可能有信用、流動性或匯率風險。',
    ],
    example: {
      title: '假設：同一個利率變化，兩種敏感度',
      steps: ['兩筆部位目前價值各 10,000 元，修正存續期間分別為 2 與 6；假設相關殖利率均上升 0.5 個百分點。', 'D = 2：價格約變動 −2 × 0.005 = −1%，約少 100 元。', 'D = 6：價格約變動 −6 × 0.005 = −3%，約少 300 元；此處忽略凸性及其他因素。'],
      conclusion: '這是敏感度試算，不是利率預測，也不是一年後含息報酬的保證。',
    },
    pitfalls: ['把存續期間當成一定要持有多久才能回本。', '把利率從 3% 變 3.5% 誤填成增加 0.5，而不是小數 0.005。'],
    glossary: [{ term: '修正存續期間', definition: '衡量債券價格對小幅殖利率變動敏感度的指標，常以年表示。' }, { term: '凸性', definition: '描述債券價格與殖利率關係的曲度，可補充存續期間的直線近似。' }],
    takeaway: '先認出是哪一種存續期間，再把它當成有條件的價格敏感度。',
    exercise: '找債券基金月報的存續期間欄位，確認是修正或有效存續期間；用利率上下各 0.25 個百分點做估算，寫出未納入的兩項風險。',
    quiz: { question: '修正存續期間為 4，殖利率下降 0.25 個百分點，忽略凸性且其他條件不變，價格約如何變化？', options: ['下降 1%', '上升 1%', '上升 4%'], answer: 1, explanation: '−4 ×（−0.0025）= +0.01，約上升 1%。這是即時價格近似，尚未計入利息或費用。' },
    sources: [
      { label: '市場先生｜債券存續期間', url: 'https://rich01.com/what-is-bond-duration-0/', kind: 'reading' },
      { label: 'FINRA｜存續期間與利率敏感度', url: 'https://syndication.finra.org/content/duration-what-interest-rate-hike-could-do-your-bond-portfolio', kind: 'official' },
    ],
  },
  {
    id: 'credit-spreads', moduleId: 'products',
    title: '多出的殖利率，在補償什麼風險？', subtitle: '信用評等與信用利差', minutes: 7, tag: '信用風險',
    objectives: ['把殖利率差換算成百分點與基點', '分辨信用評等意見與市場要求的風險補償'],
    paragraphs: [
      '比較公司債與基準債券時，信用利差是兩者殖利率的差。應盡量對齊幣別、期限與現金流特性；有提前贖回等選擇權時，還需要合適調整，不能把任意兩張債券相減就當成純信用差別。',
      '利差包含市場對違約損失的預期，也反映承擔不確定性所要求的補償與流動性等因素。因此 2% 利差不是每年必定多賺 2%，也不能直接翻譯成違約機率 2%。即使基準利率不動，信用利差擴大也可能讓既有公司債的要求殖利率上升、價格下跌。',
      '信用評等是評等機構依方法作出的信用風險意見，可能調整，也不能保證利息與本金如期支付。相同評等的債券仍可能有不同順位、擔保品與流動性。閱讀時記下評等機構、評等對象和日期，再搭配財務資料與契約條件。',
    ],
    example: {
      title: '假設：分開看基準利率與利差',
      steps: ['先假設一組同幣別、期限相近且無內含選擇權的比較：公司債殖利率 5.4%，基準公債 3.8%，簡化利差為 1.6 個百分點，也就是 160 基點。', '若基準維持 3.8%，利差擴到 2.2 個百分點，公司債要求殖利率變成 6.0%。', '利差增加 60 基點；在約定現金流不變下，用較高殖利率折現，現值會下降。實際價格變化還取決於存續期間等條件。'],
      conclusion: '看到更高殖利率，要先追查補償哪種風險，不能只把多出的數字當成獎勵。',
    },
    pitfalls: ['把高信用評等誤認為保本，或以為評等完全反映所有價格風險。', '把利差擴大當作自動買點，沒有查明信用與流動性是否惡化。'],
    glossary: [{ term: '基點（bp）', definition: '利率或比例差的單位；1 基點 = 0.01 個百分點，100 基點 = 1 個百分點。' }, { term: '信用評等', definition: '評等機構對發行人或特定債務信用風險的意見，不是報酬或償付保證。' }],
    takeaway: '利差是風險定價的線索，評等是分析意見，兩者都不是承諾。',
    exercise: '在一份債券資料中找出幣別、剩餘期限、評等機構與日期；若有利差，再查它使用哪個基準，是否調整內含選擇權。',
    quiz: { question: '符合本課簡化比較條件時，公司債殖利率 6.1%，基準債 4.3%，利差是多少？', options: ['18 基點', '1.8 基點', '180 基點'], answer: 2, explanation: '6.1% − 4.3% = 1.8 個百分點 = 180 基點；這不是公司違約機率，也不是保證能多賺的幅度。' },
    sources: [
      { label: '市場先生｜債券利差的主題閱讀', url: 'https://rich01.com/what-is-high-yield-bond-spread/', kind: 'reading' },
      { label: 'SEC｜信用評等的意義與限制', url: 'https://www.investor.gov/introduction-investing/general-resources/news-alerts/alerts-bulletins/investor-bulletins/updated-8', kind: 'official' },
      { label: 'Federal Reserve｜公司債利差與非違約因素研究', url: 'https://www.federalreserve.gov/econres/feds/effects-of-liquidity-on-the-nondefault-component-of-corporate-yield-spreads-evidence-from-intraday-transactions-data.htm', kind: 'official' },
    ],
  },
  {
    id: 'bond-fund-maturity', moduleId: 'products',
    title: '債券會到期，債券 ETF 也會嗎？', subtitle: '持續換券與目標到期型基金', minutes: 7, tag: '確認結構',
    objectives: ['區分底層債券的到期日與基金自己的期限', '辨認目標到期型產品的條件與非保本性質'],
    paragraphs: [
      '直接持有一般債券時，契約約定在到期日支付本金，前提是發行人履約；按面額買入與折溢價買入，結果也不同。一般持續運作的債券基金則持有多張債券，可能把到期款或賣券所得再投入，並依策略換入符合條件的債券。',
      '因此基金名稱中的「十年以上」等字樣，通常描述投資範圍，不是承諾你等十年就取回原本金。即使持有很久，基金仍可能維持一定利率敏感度；投資人退出時的金額依基金價值與交易條件而定，沒有和個別債券相同的固定面額還本承諾。',
      '目標到期型或固定期限債券 ETF 是需要另外辨識的類型：它依規則持有集中在某段期間到期的債券，並計畫在指定時點終止、分配剩餘資產。這也不是保本存款；最終分配仍受違約、費用、現金再投資和實際持倉影響，要讀公開說明書的終止方式。',
    ],
    example: {
      title: '假設：基金已經持有四年，不等於只剩一年',
      steps: ['甲基金的規則是持續投資剩餘期限 3 至 5 年的債券；剛買進的一張五年債，兩年後只剩三年，之後可能因規則而被替換。', '你持有甲基金四年後，組合仍可能有新的五年債，不能用最初五年減掉四年，推論一年後基金會固定還本。', '乙則明載固定期限及終止分配規則。假設你投入 10,000 元，期間收到 800 元，終止時分回 9,500 元，忽略費用且不再投資，累積總報酬為（800 + 9,500 − 10,000）÷ 10,000 = 3%。'],
      conclusion: '乙的 3% 是整段期間累積報酬，不是年化；終止時分回多少也不能事先視為保證。',
    },
    pitfalls: ['看到基金持有公債，就假設基金淨值不會跌且終有一天按原買價還本。', '把目標到期型當成普通滾動債券 ETF，或反過來認為目標到期就一定保本。'],
    glossary: [{ term: '持續換券', definition: '基金依投資規則更換債券持倉，使期限、信用或其他條件維持在目標範圍。' }, { term: '終止分配', definition: '基金結束時依規則分配扣除相關負擔後的剩餘資產，不等於償還固定面額。' }],
    takeaway: '先看基金是否有自己的終止日，再看底層債券何時到期。',
    exercise: '找一份債券 ETF 公開說明書，分別標出投資期限範圍、換券規則與基金存續或終止條款；沒有固定終止日就明確記下。',
    quiz: { question: '一般持續追蹤特定期限區間的債券 ETF，最符合哪個描述？', options: ['可能持續換券，沒有保證在你買入後某年按原本金還款', '所有底層債券到期當天都會把你的原本金還給你', '持有時間等於名稱中的年數就保證回本'], answer: 0, explanation: '底層債券的付款屬於基金資產，基金可能再投入；普通滾動型基金不因此對每位投資人產生固定本金到期承諾。目標到期型要另讀終止條款。' },
    sources: [
      { label: '市場先生｜債券 ETF 的主題閱讀', url: 'https://rich01.com/what-bond-etf/', kind: 'reading' },
      { label: 'SEC｜債券基金及其風險', url: 'https://www.investor.gov/introduction-investing/investing-basics/glossary/bond-funds-and-income-funds', kind: 'official' },
      { label: 'iShares｜固定期限債券 ETF 與一般基金的差異', url: 'https://www.ishares.com/uk/individual/en/education/getting-started-with-etfs/investor-education/fixed-term-bond-etfs', kind: 'official' },
    ],
  },
  {
    id: 'leveraged-etfs', moduleId: 'products',
    title: '每日兩倍，為什麼兩天不是兩倍？', subtitle: '槓桿、反向與每日重設', minutes: 8, tag: '理解複利',
    objectives: ['用每天的乘法計算累積報酬', '辨認每日槓桿目標與長期間報酬的不同'],
    paragraphs: [
      '每日槓桿型 ETF 追求標的「單日」報酬的指定倍數，例如正向兩倍；每日反向型則追求單日反向倍數，例如 −1 倍。這是產品目標，實際還會受追蹤差距與費用影響，且以基金定義的每日計算區間為準，不保證你任意盤中買賣都得到相同倍數。',
      '每日重設會讓第二天的漲跌套用在第一天結束後的新資產值上。跨日總報酬必須逐日相乘，不能拿指數期間漲跌直接乘以倍數。相同起點與終點之間的日報酬路徑，也可能讓槓桿產品出現不同結果。',
      '來回震盪可能使累積表現低於簡單倍數，但不能說任何路徑都必然耗損：連續同方向變動也可能讓複利結果高於簡單倍數。這不會取消風險；不利的大幅單日變動、衍生工具曝險、融資及交易成本，都可能快速侵蝕資產。反向產品也不是永久抵銷另一筆持股的保險。',
    ],
    example: {
      title: '假設：忽略費用、完全達成每日兩倍目標',
      steps: ['指數從 100 起，第一天 +10%、第二天 −10%，終值 100 × 1.1 × 0.9 = 99，兩天合計 −1%。', '每日兩倍產品從 100 起，對應 +20%、−20%，終值 100 × 1.2 × 0.8 = 96，合計 −4%，不是 −2%。', '改成連續兩天都 +10%：指數終值 121，合計 +21%；每日兩倍產品終值 144，合計 +44%，也不是簡單的 +42%。'],
      conclusion: '兩個情境都遵守每日兩倍，跨日結果卻各自偏離期間兩倍；要按路徑計算。',
    },
    pitfalls: ['把每日兩倍 ETF 當成任何持有期間都固定兩倍的普通 ETF。', '以為反向一倍產品持有一年，就一定得到市場全年報酬的相反數。'],
    glossary: [{ term: '每日重設', definition: '依產品規則調整曝險，使下一個計算日仍以當時資產值追求指定單日倍數。' }, { term: '路徑依賴', definition: '結果不只取決於起點與終點，還會受到過程中的日報酬組合影響。' }],
    takeaway: '看到倍數先找計算期間；每日目標要用每日複利驗算。',
    exercise: '自行試算指數連續兩天 −5%，以及先 +5% 再 −5% 的情境；各算一次每日兩倍產品，假設完全追蹤且無費用，寫下它和期間兩倍的差異。',
    quiz: { question: '指數連續兩天各上漲 5%，理想每日反向一倍產品從 100 起，忽略費用後的終值是多少？', options: ['89.75', '90.25', '90'], answer: 1, explanation: '每日反向一倍各下跌 5%，所以 100 × 0.95 × 0.95 = 90.25；指數兩天共漲 10.25%，直接取相反數會錯算成 89.75。' },
    sources: [
      { label: '市場先生｜槓桿 ETF 的主題閱讀', url: 'https://rich01.com/leveraged-etfs/', kind: 'reading' },
      { label: '證交所｜槓桿型與反向型 ETF 的每日目標', url: 'https://wwwc.twse.com.tw/zh/products/securities/etf/products/li.html', kind: 'official' },
    ],
  },
  {
    id: 'currency-hedging', moduleId: 'products',
    title: '用台幣買，就沒有匯率風險嗎？', subtitle: '交易幣別、資產曝險與匯率避險', minutes: 8, tag: '辨認幣別',
    objectives: ['用資產與匯率的乘法拆解本幣報酬', '確認避險針對哪一組幣別及仍存在的風險'],
    paragraphs: [
      '下單使用的交易幣別、基金公布淨值的基準幣別，以及底層資產的貨幣曝險，可能是三件事。用台幣在交易所買海外資產 ETF，不會自動消除外幣影響；需要看持倉與避險政策。以本幣衡量、未避險時，簡化報酬可寫成（1 + 外幣資產報酬）×（1 + 外幣相對本幣升貶值）− 1。',
      '匯率避險通常透過遠期外匯等工具，減少特定貨幣相對另一貨幣的波動影響。名稱寫 USD Hedged，應查明它是把哪些曝險避到美元；對以台幣生活的人，美元對台幣的影響仍可能存在。它也不會消除股票下跌、債券違約或利率變動等風險。',
      '實際避險可能有金額與調整時點落差、交易價差及對手方風險。遠期匯率也反映兩地利率等因素，因此避險的淨效果可能是成本或收益，不能當成免費固定匯率。比較時要看避險目標比例、更新頻率、費用與績效口徑，而不是只靠名稱中的「避險」二字。',
    ],
    example: {
      title: '假設：資產上漲，換回本幣卻變少',
      steps: ['以本幣價值 100 元開始，外幣資產上漲 4%，同期間該外幣相對本幣貶值 6%；忽略費用且未避險。', '本幣終值 = 100 × 1.04 × 0.94 = 97.76 元，報酬 −2.24%，不是直接相減的 −2%。', '只為比較機制，另設「完全抵銷這段匯率變動」的理想模型：資產終值先為 104 元，再假設全期避險淨成本為原本金的 0.5 元，得 103.5 元，報酬 3.5%。'],
      conclusion: '第三步是明訂假設的模型，實際避險不保證完整抵銷或固定成本；若外幣升值，避險也可能抵銷那部分好處。',
    },
    pitfalls: ['用下單幣別判定全部匯率曝險，沒有查看底層資產。', '把美元避險誤認為對台幣避險，或以為避險產品已經沒有投資風險。'],
    glossary: [{ term: '貨幣曝險', definition: '資產價值以自己的衡量幣別計算時，受到某種貨幣變動影響的程度。' }, { term: '遠期外匯', definition: '約定在未來日期依事先議定匯率交換貨幣的契約，條件會影響避險成果。' }],
    takeaway: '避險要問「哪個幣別對哪個幣別」，並確認哪些風險仍然存在。',
    exercise: '找一份海外基金月報，把交易幣別、基準幣別、主要資產曝險及避險目標分成四欄；無法從名稱判斷的欄位，回到公開說明書查證。',
    quiz: { question: '未避險、忽略費用時，外幣資產上漲 10%，外幣相對本幣貶值 10%，本幣報酬是多少？', options: ['0%', '−10%', '−1%'], answer: 2, explanation: '1.1 × 0.9 − 1 = −1%。資產與匯率是相乘關係；這裡把外幣相對本幣的價值下降定義為貶值 10%。' },
    sources: [
      { label: '市場先生｜ETF 匯率避險的主題閱讀', url: 'https://rich01.com/how-etf-forex-hedged/', kind: 'reading' },
      { label: 'iShares｜避險幣別及無法消除全部匯率風險的產品揭露', url: 'https://www.ishares.com/uk/individual/en/products/251903/IUSE', kind: 'official' },
      { label: 'Vanguard｜全球債券的匯率避險成本研究', url: 'https://www.vanguard.co.uk/content/dam/intl/europe/documents/en/going-global-with-bonds-the-benefits-of-a-more-global-fixed-income-allocation-eu-en-pro.pdf', kind: 'official' },
    ],
  },
];
